/**
 * VolumeController - Manages audio volume control for the chat system
 * Provides a slider interface for users to control audio response intensity
 */
export class VolumeController {
    constructor() {
        this.currentVolume = 1.0; // Default 100% — no in-app volume UI; device hardware is the control
        this.isSliderVisible = false;
        this.sliderHideTimeout = null;
        this.volumeStorageKey = "chatAudioVolume";
        this.volumeMonitorInterval = null;

        // No in-app volume UI: force 100% so the device's hardware volume is the
        // sole control. Deliberately do NOT read the saved localStorage value — a
        // stale slider setting (e.g. an old 60%) must never cap output below full.
        // Also wipe the stale key so it can't resurface (nothing re-saves it now).
        this.currentVolume = 1.0;
        try { localStorage.removeItem(this.volumeStorageKey); } catch (e) {}

        // Initialize DOM elements
        this.initializeDOM();

        // Start continuous volume monitoring for real-time updates
        this.startVolumeMonitoring();

        console.log(
            "VolumeController initialized with volume:",
            Math.round(this.currentVolume * 100) + "%",
        );
    }

    /**
     * Initialize DOM elements and event listeners
     */
    initializeDOM() {
        // Get DOM elements
        this.volumeToggle = document.getElementById("volume-toggle");
        this.volumeSliderContainer = document.getElementById(
            "volume-slider-container",
        );
        this.volumeSlider = document.getElementById("volume-slider");
        this.volumePercentage = document.querySelector(".volume-percentage");

        if (
            !this.volumeToggle ||
            !this.volumeSliderContainer ||
            !this.volumeSlider
        ) {
            console.warn("Volume control elements not found in DOM");
            return;
        }

        // Set initial slider value
        this.volumeSlider.value = Math.round(this.currentVolume * 100);
        this.updateVolumeDisplay();

        // Set up event listeners
        this.setupEventListeners();

        console.log("Volume control DOM elements initialized");
    }

    /**
     * Set up event listeners for volume controls with hover functionality
     */
    setupEventListeners() {
        // Get the container for hover events
        const volumeContainer = this.volumeToggle?.closest(
            ".volume-control-container",
        );

        // Hover functionality - show slider on hover
        if (volumeContainer) {
            volumeContainer.addEventListener("mouseenter", () => {
                this.showVolumeSlider();
                this.clearSliderHideTimer();
            });

            volumeContainer.addEventListener("mouseleave", () => {
                this.resetSliderHideTimer();
            });
        }

        // Volume slider input - enhanced for real-time responsiveness
        if (this.volumeSlider) {
            this.volumeSlider.addEventListener("input", (e) => {
                const value = parseInt(e.target.value);
                this.setVolume(value / 100);
                this.clearSliderHideTimer(); // Keep visible while using

                // AGGRESSIVE: Force immediate volume update on ALL audio elements
                this.forceVolumeUpdateOnAllAudio();
            });

            // Additional change event for final volume confirmation
            this.volumeSlider.addEventListener("change", (e) => {
                const value = parseInt(e.target.value);
                this.setVolume(value / 100);

                // AGGRESSIVE: Double-check volume application after slider change
                this.forceVolumeUpdateOnAllAudio();
            });
        }

        // Prevent slider container clicks from bubbling
        if (this.volumeSliderContainer) {
            this.volumeSliderContainer.addEventListener("click", (e) => {
                e.stopPropagation();
            });

            // Keep slider visible when hovering over it
            this.volumeSliderContainer.addEventListener("mouseenter", () => {
                this.clearSliderHideTimer();
            });

            this.volumeSliderContainer.addEventListener("mouseleave", () => {
                this.resetSliderHideTimer();
            });
        }

        console.log("Volume control hover event listeners set up");
    }

    /**
     * Toggle volume slider visibility
     */
    toggleVolumeSlider() {
        if (this.isSliderVisible) {
            this.hideVolumeSlider();
        } else {
            this.showVolumeSlider();
        }
    }

    /**
     * Show volume slider (no animation)
     */
    showVolumeSlider() {
        if (!this.volumeSliderContainer) return;

        this.volumeSliderContainer.classList.remove("d-none");
        this.volumeSliderContainer.style.display = "flex";
        this.isSliderVisible = true;

        // Auto-hide after brief delay for hover behavior
        this.resetSliderHideTimer();

        console.log("Volume slider shown");
    }

    /**
     * Hide volume slider (no animation)
     */
    hideVolumeSlider() {
        if (!this.volumeSliderContainer) return;

        this.volumeSliderContainer.classList.add("d-none");
        this.volumeSliderContainer.style.display = "none";
        this.isSliderVisible = false;
        this.clearSliderHideTimer();

        console.log("Volume slider hidden");
    }

    /**
     * Reset the auto-hide timer for the slider (hover version)
     */
    resetSliderHideTimer() {
        this.clearSliderHideTimer();
        this.sliderHideTimeout = setTimeout(() => {
            this.hideVolumeSlider();
        }, 500); // Hide after 0.5 seconds for hover behavior
    }

    /**
     * Clear the auto-hide timer
     */
    clearSliderHideTimer() {
        if (this.sliderHideTimeout) {
            clearTimeout(this.sliderHideTimeout);
            this.sliderHideTimeout = null;
        }
    }

    /**
     * Set volume level (0.15 to 1.0, minimum 15%)
     * @param {number} volume - Volume level between 0.15 and 1.0
     */
    setVolume(volume) {
        // Clamp volume between 0.15 (15%) and 1 (100%) - minimum 15%
        this.currentVolume = Math.max(0.15, Math.min(1, volume));

        // Update display
        this.updateVolumeDisplay();

        // Save to localStorage
        this.saveVolume();

        // Update volume icon based on level
        this.updateVolumeIcon();

        // Update volume for any currently playing audio elements
        this.updateActiveAudioVolume();

        console.log(
            "Volume set to:",
            Math.round(this.currentVolume * 100) + "% (minimum 15%)",
        );
    }

    /**
     * Update volume for currently active audio elements
     */
    updateActiveAudioVolume() {
        let audioElementsUpdated = 0;

        // Update volume for TTS audio if available
        if (window.chatController?.tts?.currentAudio) {
            window.chatController.tts.currentAudio.volume = this.currentVolume;
            audioElementsUpdated++;
            console.log("Updated TTS audio volume");
        }

        // Update volume for chunked audio if available
        if (window.chunkedChatIntegration?.chunkedPlayer?.currentAudio) {
            window.chunkedChatIntegration.chunkedPlayer.currentAudio.volume =
                this.currentVolume;
            audioElementsUpdated++;
            console.log("Updated chunked audio volume");
        }

        // Update volume for single audio if available
        if (
            window.chunkedChatIntegration?.singleAudioElement &&
            !window.chunkedChatIntegration.singleAudioElement.paused
        ) {
            window.chunkedChatIntegration.singleAudioElement.volume =
                this.currentVolume;
            audioElementsUpdated++;
            console.log("Updated single audio volume");
        }

        // Update volume for ChatController's active audio chunks (be more aggressive)
        if (window.chatController?.activeAudioChunks) {
            window.chatController.activeAudioChunks.forEach((audio, index) => {
                if (audio && audio.currentTime !== undefined) {
                    // Update volume regardless of paused state for real-time control
                    audio.volume = this.currentVolume;
                    audioElementsUpdated++;
                    console.log(
                        `Updated chunk ${index} volume to ${Math.round(this.currentVolume * 100)}%`,
                    );
                }
            });
        }

        // Aggressively check for ALL audio elements on the page
        const allAudioElements = document.querySelectorAll("audio");
        allAudioElements.forEach((audio, index) => {
            if (audio.currentTime !== undefined) {
                // Update all audio elements, even if paused or ended
                const oldVolume = audio.volume;
                audio.volume = this.currentVolume;
                if (oldVolume !== this.currentVolume) {
                    audioElementsUpdated++;
                    console.log(
                        `Updated audio element ${index} volume: ${Math.round(oldVolume * 100)}% → ${Math.round(this.currentVolume * 100)}%`,
                    );
                }
            }
        });

        // Also try to find any audio elements created with blob URLs
        try {
            const mediaElements = document.querySelectorAll(
                'audio[src^="blob:"]',
            );
            mediaElements.forEach((audio, index) => {
                audio.volume = this.currentVolume;
                audioElementsUpdated++;
                console.log(
                    `Updated blob audio ${index} volume to ${Math.round(this.currentVolume * 100)}%`,
                );
            });
        } catch (error) {
            console.log("Error updating blob audio elements:", error);
        }

        if (audioElementsUpdated > 0) {
            console.log(
                `🔊 Real-time volume update: ${audioElementsUpdated} audio elements updated to ${this.getVolumePercentage()}`,
            );
        } else {
            console.log(
                "⚠️ No active audio elements found to update volume - may need to wait for audio to start",
            );
        }
    }

    /**
     * Get current volume level
     * @returns {number} Current volume (0.0 to 1.0)
     */
    getVolume() {
        return this.currentVolume;
    }

    /**
     * Update volume display percentage
     */
    updateVolumeDisplay() {
        if (this.volumePercentage) {
            this.volumePercentage.textContent =
                Math.round(this.currentVolume * 100) + "%";
        }

        if (this.volumeSlider) {
            this.volumeSlider.value = Math.round(this.currentVolume * 100);
        }
    }

    /**
     * Update volume icon based on current volume level
     */
    updateVolumeIcon() {
        if (!this.volumeToggle) return;

        const icon = this.volumeToggle.querySelector("i");
        if (!icon) return;

        // Remove existing volume classes
        icon.classList.remove(
            "fa-volume-off",
            "fa-volume-down",
            "fa-volume-up",
        );

        // Add appropriate icon based on volume level
        if (this.currentVolume === 0) {
            icon.classList.add("fa-volume-off");
        } else if (this.currentVolume < 0.5) {
            icon.classList.add("fa-volume-down");
        } else {
            icon.classList.add("fa-volume-up");
        }
    }

    /**
     * Get current volume level
     * @returns {number} Current volume level (0.0 to 1.0)
     */
    getCurrentVolume() {
        return this.currentVolume;
    }

    /**
     * Apply volume to an audio element
     * @param {HTMLAudioElement} audioElement - Audio element to apply volume to
     */
    applyVolumeToAudio(audioElement) {
        if (audioElement && typeof audioElement.volume !== "undefined") {
            audioElement.volume = this.currentVolume;
            console.log(
                "Applied volume",
                Math.round(this.currentVolume * 100) + "% to audio element",
            );
        }
    }

    /**
     * Mute/unmute audio
     */
    toggleMute() {
        if (this.currentVolume > 0) {
            // Store current volume and mute
            this.previousVolume = this.currentVolume;
            this.setVolume(0);
        } else {
            // Restore previous volume or set to 50% if no previous volume
            const restoreVolume = this.previousVolume || 0.5;
            this.setVolume(restoreVolume);
        }
    }

    /**
     * Save volume to localStorage
     */
    saveVolume() {
        try {
            localStorage.setItem(
                this.volumeStorageKey,
                this.currentVolume.toString(),
            );
        } catch (error) {
            console.warn("Failed to save volume to localStorage:", error);
        }
    }

    /**
     * Load saved volume from localStorage
     */
    loadSavedVolume() {
        try {
            const savedVolume = localStorage.getItem(this.volumeStorageKey);
            if (savedVolume !== null) {
                const volume = parseFloat(savedVolume);
                if (!isNaN(volume) && volume >= 0.15 && volume <= 1) {
                    this.currentVolume = volume;
                    console.log(
                        "Loaded saved volume:",
                        Math.round(this.currentVolume * 100) + "%",
                    );
                } else if (!isNaN(volume) && volume < 0.15) {
                    // Upgrade saved volume that's below minimum to 15%
                    this.currentVolume = 0.15;
                    this.saveVolume(); // Update storage with new minimum
                    console.log("Upgraded saved volume to minimum 15%");
                }
            }
        } catch (error) {
            console.warn("Failed to load volume from localStorage:", error);
        }
    }

    /**
     * Get volume level as percentage string
     * @returns {string} Volume percentage (e.g., "80%")
     */
    getVolumePercentage() {
        return Math.round(this.currentVolume * 100) + "%";
    }

    /**
     * Check if audio is muted (at minimum volume)
     * @returns {boolean} True if at minimum volume (15%)
     */
    isMuted() {
        return this.currentVolume <= 0.15;
    }

    /**
     * Start continuous volume monitoring for real-time updates
     */
    startVolumeMonitoring() {
        // Clear any existing monitor
        if (this.volumeMonitorInterval) {
            clearInterval(this.volumeMonitorInterval);
        }

        // Monitor every 200ms for more responsive real-time updates
        this.volumeMonitorInterval = setInterval(() => {
            let audioElementsUpdated = 0;

            // Method 1: Check standard audio elements
            const audioElements = document.querySelectorAll("audio");
            audioElements.forEach((audio, index) => {
                // Update ALL audio elements regardless of their state
                if (audio && typeof audio.volume !== "undefined") {
                    if (Math.abs(audio.volume - this.currentVolume) > 0.01) {
                        audio.volume = this.currentVolume;
                        audioElementsUpdated++;
                        console.log(
                            `🔄 Updated audio element ${index} volume to ${Math.round(this.currentVolume * 100)}%`,
                        );
                    }
                }
            });

            // Method 2: Check ChatController's active audio chunks more aggressively
            if (window.chatController?.activeAudioChunks) {
                window.chatController.activeAudioChunks.forEach(
                    (audio, index) => {
                        if (audio && typeof audio.volume !== "undefined") {
                            if (
                                Math.abs(audio.volume - this.currentVolume) >
                                0.01
                            ) {
                                audio.volume = this.currentVolume;
                                audioElementsUpdated++;
                                console.log(
                                    `🔄 Updated chunk ${index} volume to ${Math.round(this.currentVolume * 100)}%`,
                                );
                            }
                        }
                    },
                );
            }

            // Method 3: Check all blob URL audio elements
            try {
                const allMediaElements = Array.from(
                    document.getElementsByTagName("audio"),
                );
                allMediaElements.forEach((audio, index) => {
                    if (audio.src && audio.src.startsWith("blob:")) {
                        if (
                            Math.abs(audio.volume - this.currentVolume) > 0.01
                        ) {
                            audio.volume = this.currentVolume;
                            audioElementsUpdated++;
                            console.log(
                                `🔄 Updated blob audio ${index} volume to ${Math.round(this.currentVolume * 100)}%`,
                            );
                        }
                    }
                });
            } catch (error) {
                // Silent fail for blob audio check
            }

            if (audioElementsUpdated > 0) {
                console.log(
                    `🔊 Real-time monitor: Updated ${audioElementsUpdated} audio elements`,
                );
            }
        }, 200);

        console.log(
            "🔄 Aggressive volume monitoring started (200ms intervals)",
        );
    }

    /**
     * Stop volume monitoring
     */
    stopVolumeMonitoring() {
        if (this.volumeMonitorInterval) {
            clearInterval(this.volumeMonitorInterval);
            this.volumeMonitorInterval = null;
            console.log("🔄 Volume monitoring stopped");
        }
    }

    /**
     * AGGRESSIVE: Force immediate volume update on ALL audio elements
     * This is called directly from slider events for real-time control
     */
    forceVolumeUpdateOnAllAudio() {
        let updatedCount = 0;

        // Method 1: All audio elements on page
        const allAudio = document.querySelectorAll("audio");
        allAudio.forEach((audio, index) => {
            if (audio && typeof audio.volume !== "undefined") {
                audio.volume = this.currentVolume;
                updatedCount++;
                console.log(
                    `🎚️ Slider event: Set audio ${index} to ${Math.round(this.currentVolume * 100)}%`,
                );
            }
        });

        // Method 2: ChatController active chunks
        if (window.chatController?.activeAudioChunks) {
            window.chatController.activeAudioChunks.forEach((audio, index) => {
                if (audio && typeof audio.volume !== "undefined") {
                    audio.volume = this.currentVolume;
                    updatedCount++;
                    console.log(
                        `🎚️ Slider event: Set chunk ${index} to ${Math.round(this.currentVolume * 100)}%`,
                    );
                }
            });
        }

        // Method 3: Search for currently playing audio by iterating through all media elements
        try {
            const mediaElements = document.getElementsByTagName("audio");
            for (let i = 0; i < mediaElements.length; i++) {
                const audio = mediaElements[i];
                if (audio && !audio.paused && audio.currentTime > 0) {
                    audio.volume = this.currentVolume;
                    updatedCount++;
                    console.log(
                        `🎚️ Slider event: Set playing audio ${i} to ${Math.round(this.currentVolume * 100)}%`,
                    );
                }
            }
        } catch (error) {
            console.warn("Error in aggressive audio update:", error);
        }

        // Method 4: Global audio search
        if (window.currentlyPlayingAudio) {
            window.currentlyPlayingAudio.volume = this.currentVolume;
            updatedCount++;
            console.log(
                `🎚️ Slider event: Updated global audio to ${Math.round(this.currentVolume * 100)}%`,
            );
        }

        console.log(
            `🎚️ IMMEDIATE UPDATE: Applied ${Math.round(this.currentVolume * 100)}% volume to ${updatedCount} audio elements`,
        );

        // Also start monitoring to catch any new audio that appears
        this.startVolumeMonitoring();
    }
}

// Export for global access
window.VolumeController = VolumeController;
